/**
 * Data layer for recipes (the recipes feature, Phase 1).
 *
 * A "recipe" is a reusable, named generation spec: a prompt plus a target scope,
 * output kind and rubric that the generate path resolves into one piece of
 * content. Global "made by holito" built-ins (organization_id null) are seeded
 * via migration and world-readable; user recipes (organization_id not null)
 * belong to an org and are gated by membership (private ones only to their
 * creator). A row is a built-in iff its organization_id is null, surfaced here as
 * the derived {@link Recipe.isBuiltin} flag (no origin column, no hardcoded id).
 *
 * Rows are read/written directly through the authenticated browser Supabase
 * client. RLS (see the recipes migration) gates every row, so the service does
 * no authz of its own: a row the caller cannot see simply does not come back,
 * and a write the caller cannot make is rejected by the policy. Every function
 * takes the client as its first argument so it can be unit-tested with a fake,
 * mirroring {@link services/askThreadService}.
 *
 * @module services/recipeService
 */

import type { SupabaseClient } from '@supabase/supabase-js';

/** The scope a recipe targets. Phase 1 user recipes are always 'conversation'. */
export type RecipeScope = 'conversation' | 'campaign';

/** The kind of artifact a recipe produces. Phase 1 user recipes are 'html'. */
export type RecipeOutputKind = 'html' | 'structured';

/** Pluggable quality rubric. Phase 1 user recipes use 'neutral'. */
export type RecipeRubric = 'marketing' | 'report' | 'summary' | 'email' | 'neutral';

/**
 * Who a recipe is shared with. There is no 'public': a platform-public /
 * shareable mechanism is a deliberate FUTURE follow-up (keyed on holito brand
 * URLs) and is intentionally not built yet. The two-member type is also a
 * compile-time guard that no code path can request platform-public.
 */
export type RecipeVisibility = 'private' | 'org';

/**
 * Canonical holito-curated use-case categories.
 * Only built-in recipes carry a category in v1; org recipes default to null.
 * Extending the set requires a migration that widens the DB CHECK constraint.
 */
export const RECIPE_CATEGORIES = ['marketing', 'sales', 'product', 'research', 'support'] as const;

/** A single holito-curated use-case category value. */
export type RecipeCategory = typeof RECIPE_CATEGORIES[number];

/** Human-readable label for each {@link RecipeCategory}, suitable for display in the UI. */
export const RECIPE_CATEGORY_LABELS: Record<RecipeCategory, string> = {
  marketing: 'Marketing',
  sales: 'Sales',
  product: 'Product',
  research: 'Research',
  support: 'Support',
};

/** A recipe row as the app consumes it (camelCase). */
export interface Recipe {
  /** Row id. */
  id: string;
  /** Owning org, or null for a global built-in. */
  organizationId: string | null;
  /**
   * Whether this is a global "made by holito" built-in, derived from
   * organization_id being null. The unforgeable built-in signal: users cannot
   * write an org-NULL row (RLS), so there is no origin column or hardcoded id.
   */
  isBuiltin: boolean;
  /** Reserved for workspace scoping; always null in Phase 1. */
  workspaceId: string | null;
  /** Stable key for built-ins; null for user recipes. */
  slug: string | null;
  /** Display name. */
  name: string;
  /** Compare prior entries with the same email in this organization. */
  crossCheckIdentity?: boolean;
  /** Ground external context with cited public web sources. */
  useWebResearch?: boolean;
  /** Author-editable one-line description; null when never set. */
  description: string | null;
  /** The natural-language generation instruction. */
  prompt: string;
  /** Target scope. */
  scope: RecipeScope;
  /** Output kind. */
  outputKind: RecipeOutputKind;
  /** Quality rubric. */
  rubric: RecipeRubric;
  /** Author label shown on the card ('holito' for built-ins). */
  authorName: string | null;
  /** Sharing scope. */
  visibility: RecipeVisibility;
  /** Optional model override. */
  model: string | null;
  /** Soft-delete flag. */
  isArchived: boolean;
  /** Creator user id (null for built-ins). */
  createdBy: string | null;
  /** ISO creation timestamp. */
  createdAt: string;
  /** ISO last-updated timestamp. */
  updatedAt: string;
  /**
   * Holito-curated use-case category (one of {@link RECIPE_CATEGORIES}).
   * Set on built-ins; null for user recipes in v1.
   */
  category: string | null;
  /**
   * Free-form personal tags the owner applied to this recipe.
   * Empty array when no tags have been set.
   */
  tags: string[];
}

/** Fields accepted when creating a user recipe. */
export interface CreateRecipeInput {
  /** Display name. */
  name: string;
  /** Compare prior entries with the same email in this organization. */
  crossCheckIdentity?: boolean;
  /** Ground external context with cited public web sources. */
  useWebResearch?: boolean;
  /** Optional author-editable one-line description (empty/whitespace stores null). */
  description?: string | null;
  /** The natural-language generation instruction. */
  prompt: string;
  /** Target scope (Phase 1: 'conversation'). */
  scope: RecipeScope;
  /** Output kind (Phase 1: 'html'). */
  outputKind: RecipeOutputKind;
  /** Quality rubric (Phase 1: 'neutral'). */
  rubric: RecipeRubric;
  /** Sharing scope: Private (creator only) or Team. */
  visibility: RecipeVisibility;
  /** Owning org id. */
  organizationId: string;
  /** Author label shown on the card. */
  authorName?: string | null;
  /** Optional model override. */
  model?: string | null;
  /** Free-form personal tags. Defaults to [] when not supplied. */
  tags?: string[];
}

/** Fields accepted when editing a user recipe. */
export interface UpdateRecipeInput {
  /** New display name. */
  name?: string;
  /** Enable prior-entry evidence. */
  crossCheckIdentity?: boolean;
  /** Enable cited web evidence. */
  useWebResearch?: boolean;
  /** New one-line description (empty/whitespace clears it to null). Omit to leave unchanged. */
  description?: string | null;
  /** New generation instruction. */
  prompt?: string;
  /** New sharing scope: Private (creator only) or Team. */
  visibility?: RecipeVisibility;
  /** Replacement set of free-form personal tags. Omit to leave tags unchanged. */
  tags?: string[];
}

/** The raw snake_case columns selected from public.recipes. */
const RECIPE_COLUMNS =
  'id, organization_id, workspace_id, slug, name, description, prompt, scope, output_kind, ' +
  'rubric, author_name, visibility, model, is_archived, created_by, ' +
  'created_at, updated_at, category, tags, cross_check_identity, use_web_research';

/** A recipes row as PostgREST returns it. */
interface RecipeRow {
  cross_check_identity?: boolean;
  use_web_research?: boolean;
  id: string;
  organization_id: string | null;
  workspace_id: string | null;
  slug: string | null;
  name: string;
  description: string | null;
  prompt: string;
  scope: RecipeScope;
  output_kind: RecipeOutputKind;
  rubric: RecipeRubric;
  author_name: string | null;
  visibility: RecipeVisibility;
  model: string | null;
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Curated use-case category; null when not set. */
  category: string | null;
  /** Free-form personal tags; empty array when none. */
  tags: string[];
}

/** Map a raw recipes row to the camelCase {@link Recipe} shape. */
function mapRow(row: RecipeRow): Recipe {
  return {
    id: row.id,
    crossCheckIdentity: row.cross_check_identity ?? false,
    useWebResearch: row.use_web_research ?? false,
    organizationId: row.organization_id,
    // A row is a global built-in iff it has no owning org.
    isBuiltin: row.organization_id === null,
    workspaceId: row.workspace_id,
    slug: row.slug,
    name: row.name,
    description: row.description ?? null,
    prompt: row.prompt,
    scope: row.scope,
    outputKind: row.output_kind,
    rubric: row.rubric,
    authorName: row.author_name,
    visibility: row.visibility,
    model: row.model,
    isArchived: row.is_archived,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    category: row.category,
    // Defensively guard against null from older rows (column defaults to '{}').
    tags: row.tags ?? [],
  };
}

/**
 * List the recipes visible in one org: global built-ins (organization_id IS
 * NULL) plus the active org's own recipes. RLS would permit every org the caller
 * belongs to, so the active-org filter is what keeps a user's recipes from
 * bleeding across an org switch. With no active org, only built-ins load.
 * Archived recipes are excluded. Ordered newest activity first.
 *
 * @param supabase - Authenticated browser client.
 * @param organizationId - The active org to scope user recipes to, or null/undefined for built-ins only.
 */
export async function listRecipes(
  supabase: SupabaseClient,
  organizationId?: string | null,
): Promise<Recipe[]> {
  let query = supabase.from('recipes').select(RECIPE_COLUMNS).eq('is_archived', false);

  query = organizationId
    ? query.or(`organization_id.is.null,organization_id.eq.${organizationId}`)
    : query.is('organization_id', null);

  const { data, error } = await query.order('updated_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Failed to load recipes');
  }

  // `.select()` is called with a const column string, which resolves to the
  // generic string overload rather than a typed row, so cast through unknown.
  return ((data ?? []) as unknown as RecipeRow[]).map(mapRow);
}

/**
 * Create a user recipe in the caller's org. The non-null `organization_id` is
 * what makes the row a user recipe: RLS rejects org-NULL writes, so the caller
 * can never forge a global built-in. `created_by` defaults to `auth.uid()`
 * server-side, satisfying the insert policy.
 */
export async function createRecipe(
  supabase: SupabaseClient,
  input: CreateRecipeInput,
): Promise<Recipe> {
  const { data, error } = await supabase
    .from('recipes')
    .insert({
      organization_id: input.organizationId,
      workspace_id: null,
      name: input.name,
      description: input.description?.trim() ? input.description.trim() : null,
      prompt: input.prompt,
      scope: input.scope,
      output_kind: input.outputKind,
      rubric: input.rubric,
      author_name: input.authorName ?? null,
      visibility: input.visibility,
      model: input.model ?? null,
      tags: input.tags ?? [],
      cross_check_identity: input.crossCheckIdentity ?? false,
      use_web_research: input.useWebResearch ?? false,
    })
    .select(RECIPE_COLUMNS)
    .single();

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create recipe');
  }

  return mapRow(data as unknown as RecipeRow);
}

/** Edit a user recipe's name, prompt, or visibility. Built-ins are immutable. */
export async function updateRecipe(
  supabase: SupabaseClient,
  id: string,
  patch: UpdateRecipeInput,
): Promise<Recipe> {
  const { data, error } = await supabase
    .from('recipes')
    .update({
      ...(patch.crossCheckIdentity !== undefined ? { cross_check_identity: patch.crossCheckIdentity } : {}),
      ...(patch.useWebResearch !== undefined ? { use_web_research: patch.useWebResearch } : {}),
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.description !== undefined
        ? { description: patch.description?.trim() ? patch.description.trim() : null }
        : {}),
      ...(patch.prompt !== undefined ? { prompt: patch.prompt } : {}),
      ...(patch.visibility !== undefined ? { visibility: patch.visibility } : {}),
      ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
    })
    .eq('id', id)
    .select(RECIPE_COLUMNS)
    .single();

  if (error || !data) {
    throw new Error(error?.message || 'Failed to update recipe');
  }

  return mapRow(data as unknown as RecipeRow);
}

/** Soft-delete a user recipe by flagging it archived (hidden from listings). */
export async function archiveRecipe(supabase: SupabaseClient, id: string): Promise<void> {
  const { error } = await supabase.from('recipes').update({ is_archived: true }).eq('id', id);

  if (error) {
    throw new Error(error.message || 'Failed to archive recipe');
  }
}
